import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { faEllipsisVertical, faEye, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { ReceptionListItem, canDeleteReception, canEditReception } from '../../models/Reception';

interface DropdownActionsProps {
  reception: ReceptionListItem;
  onEdit?: (reception: ReceptionListItem) => void;
  onDelete?: (reception: ReceptionListItem) => void;
}

/**
 * Menú desplegable de acciones para una recepción
 */
export const DropdownActions: React.FC<DropdownActionsProps> = ({ reception, onEdit, onDelete }) => {
  const { t } = useTranslation('receptionsModule');

  const canEdit = canEditReception(reception.status);
  const canDelete = canDeleteReception(reception);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="reception-actions-trigger">
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
          <span className="sr-only">{t('table.actions')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Ver detalle - siempre disponible, navega a la página individual */}
        <DropdownMenuLinkItem to={`/receptions/${reception.id}`} data-testid="action-view">
          <FontAwesomeIcon icon={faEye} className="mr-2 h-4 w-4" />
          {t('actions.view')}
        </DropdownMenuLinkItem>

        {/* Editar - solo si no está cerrada */}
        {onEdit && canEdit && (
          <DropdownMenuItem onClick={() => onEdit(reception)} data-testid="action-edit">
            <FontAwesomeIcon icon={faPen} className="mr-2 h-4 w-4" />
            {t('actions.edit')}
          </DropdownMenuItem>
        )}

        {/* Eliminar - solo si está sin identificar */}
        {onDelete && canDelete && (
          <DropdownMenuItem
            onClick={() => onDelete(reception)}
            className="text-destructive focus:text-destructive"
            data-testid="action-delete"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2 h-4 w-4" />
            {t('actions.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DropdownActions;
